import { Badge, Button, Card, Field, Markdown } from "@procurement-agents/ui";
import { ApiError, isFullConfig, type AgentRole, type AgentSettingsT, type InstructionOverlayT, type LintItem, type OverlayLint } from "@procurement-agents/scope-api";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useScopeApi } from "../api";
import { relativeTime } from "../labels";
import { DraftGate, useAgents } from "./AgentsApp";
import { AGENT_LABEL } from "./labels";

const EMPTY: InstructionOverlayT = { text: "", examples: [], lint: null };
const LINT_DEBOUNCE_MS = 1500;
const SAVE_DEBOUNCE_MS = 700;

function rolesFor(overview: { role: string; agent_roles: AgentRole[] }): AgentRole[] {
  return overview.role === "sme" ? ["drafting", "qa"] : overview.agent_roles;
}

/** The workflow's agents as a directory: what each one does, the model it uses and whether this customer has told it
 *  anything. Read-only; configuring an agent is a click away. */
export function AgentsDirectory({ base }: { base: string }) {
  const { overview, shown } = useAgents();
  const full = isFullConfig(shown) ? shown : null;
  const dflt = overview.default_model;
  const model = (role: AgentRole) => {
    const id = full?.settings[role]?.model_id;
    const m = id ? overview.catalogue[id] : null;
    return m ? `${m.provider}/${m.model}` : `${dflt.provider}/${dflt.model} (default)`;
  };
  return (
    <section className="agent-directory">
      <DraftGate />
      <p className="muted small">The agents in this workflow, in the order they run. Select one to read or change how it is configured.</p>
      <table className="rows agent-list">
        <thead><tr><th>Agent</th><th>What it does</th>{full && overview.role !== "sme" && <th>Model</th>}<th>Tenant instructions</th></tr></thead>
        <tbody>
          {rolesFor(overview).map((role) => {
            const ov = (shown.overlays ?? {})[role];
            const lint = ov?.lint?.items ?? [];
            const blocking = lint.filter((i) => i.severity === "blocking").length;
            return (
              <tr key={role}>
                <th scope="row"><Link to={`${base}/agents/${role}`}>{AGENT_LABEL[role].name}</Link></th>
                <td className="muted">{AGENT_LABEL[role].blurb}</td>
                {full && overview.role !== "sme" && <td className="muted small">{model(role)}</td>}
                <td>
                  {ov?.text.trim()
                    ? <span className="row small">{ov.text.trim().split(/\s+/).length} words {blocking ? <Badge tone="bad">{blocking} blocking</Badge> : ov.lint ? <Badge tone="good">linted</Badge> : <Badge tone="warn">not linted</Badge>}</span>
                    : <span className="muted small">none</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

/** One agent: model, temperature, the role's extras, and the overlay editor with the core shown read-only above it. */
export function AgentCard({ base }: { base: string }) {
  const { overview, shown, editable } = useAgents();
  const { role } = useParams();
  const roles = rolesFor(overview);
  const full = isFullConfig(shown) ? shown : null;
  if (!role || !roles.includes(role as AgentRole)) return <p className="muted">No such agent in this workflow. <Link to={`${base}/agents`}>All agents</Link></p>;
  const r = role as AgentRole;
  return (
    <section className="agent-cards">
      <Link to={`${base}/agents`} className="back-link">← All agents</Link>
      <DraftGate />
      <Card title={<>{AGENT_LABEL[r].name} <span className="muted small">{AGENT_LABEL[r].blurb}</span></>} className="agent-card">
        {full && overview.role !== "sme" && <Settings role={r} settings={full.settings[r] ?? {}} editable={editable} />}
        <OverlayEditor role={r} overlay={(shown.overlays ?? {})[r] ?? EMPTY} editable={editable} />
      </Card>
    </section>
  );
}

function Settings({ role, settings, editable }: { role: AgentRole; settings: AgentSettingsT; editable: boolean }) {
  const { overview, apply } = useAgents();
  const set = (field: keyof AgentSettingsT, value: unknown) => void apply(value === null || value === "" ? [{ op: "remove", path: `/settings/${role}/${field}` }] : [{ op: "replace", path: `/settings/${role}/${field}`, value }]);
  const num = (field: keyof AgentSettingsT, e: React.ChangeEvent<HTMLInputElement>) => set(field, e.target.value === "" ? null : Number(e.target.value));
  const dflt = overview.default_model;
  return (
    <div className="row wrap agent-settings">
      <Field label="Model">
        <select aria-label={`Model for ${AGENT_LABEL[role].name}`} value={settings.model_id ?? ""} disabled={!editable} onChange={(e) => set("model_id", e.target.value || null)}>
          <option value="">Tenant default ({dflt.provider}/{dflt.model})</option>
          {Object.entries(overview.catalogue).map(([id, m]) => <option key={id} value={id}>{id} ({m.provider}/{m.model})</option>)}
        </select>
      </Field>
      <Field label="Temperature"><input type="number" min={0} max={2} step={0.1} aria-label={`Temperature for ${AGENT_LABEL[role].name}`} defaultValue={settings.temperature ?? ""} disabled={!editable} onBlur={(e) => num("temperature", e)} placeholder="default" /></Field>
      {(role === "drafting" || role === "qa") && <Field label="Concurrency"><input type="number" min={1} max={16} aria-label={`Concurrency for ${AGENT_LABEL[role].name}`} defaultValue={settings.concurrency ?? ""} disabled={!editable} onBlur={(e) => num("concurrency", e)} placeholder="default" /></Field>}
      {role === "drafting" && <Field label="Level of detail"><select aria-label="Level of detail" value={settings.verbosity ?? ""} disabled={!editable} onChange={(e) => set("verbosity", e.target.value || null)}><option value="">the customer's rules</option><option value="minimal">minimal — short and to the point</option><option value="standard">standard — complete, without padding</option><option value="full">full — with context and rationale</option></select></Field>}
      {role === "drafting" && <Field label="Practice guidance"><select aria-label="Practice guidance source" value={settings.practices_source ?? ""} disabled={!editable} onChange={(e) => set("practices_source", e.target.value || null)}><option value="">tenant default</option><option value="model">model</option><option value="corpus">corpus</option><option value="hybrid">hybrid</option></select></Field>}
      {role === "drafting" && <Field label="SME confidence threshold"><input type="number" min={0} max={1} step={0.05} aria-label="SME confidence threshold" defaultValue={settings.sme_confidence_threshold ?? ""} disabled={!editable} onBlur={(e) => num("sme_confidence_threshold", e)} placeholder="default" /></Field>}
      {role === "market_research" && <Field label="Research mode"><select aria-label="Research mode" value={settings.research_mode ?? ""} disabled={!editable} onChange={(e) => set("research_mode", e.target.value || null)}><option value="">tenant default</option><option value="model">model</option><option value="web">web search</option></select></Field>}
      {role === "market_research" && <Field label="Max search rounds"><input type="number" min={1} max={10} aria-label="Max search rounds" defaultValue={settings.max_search_rounds ?? ""} disabled={!editable} onBlur={(e) => num("max_search_rounds", e)} placeholder="default" /></Field>}
      {role === "qa" && <Field label="Max fix rounds"><input type="number" min={0} max={5} aria-label="Max QA fix rounds" defaultValue={settings.qa_max_rounds ?? ""} disabled={!editable} onBlur={(e) => num("qa_max_rounds", e)} placeholder="default" /></Field>}
      {role === "qa" && <Field label="QA rules (one per line; replaces the tenant list)" hint="Each rule becomes one lens the QA agent checks every section against."><textarea rows={3} aria-label="QA rules" defaultValue={(settings.qa_rules ?? []).join("\n")} disabled={!editable} onBlur={(e) => { const v = e.target.value.split("\n").map((x) => x.trim()).filter(Boolean); set("qa_rules", v.length ? v : null); }} /></Field>}
      {role === "editing" && <Field label="Tool iteration cap"><input type="number" min={1} max={30} aria-label="Tool iteration cap" defaultValue={settings.tool_iteration_cap ?? ""} disabled={!editable} onBlur={(e) => num("tool_iteration_cap", e)} placeholder="default" /></Field>}
    </div>
  );
}

/** Markdown overlay under the read-only core; saves on a pause, lints on a longer pause; examples as input/output pairs; comments in the margin. */
function OverlayEditor({ role, overlay, editable }: { role: AgentRole; overlay: InstructionOverlayT; editable: boolean }) {
  const { api, toast } = useScopeApi();
  const { overview, shown, cores, apply, refresh } = useAgents();
  const [text, setText] = useState(overlay.text);
  const [lint, setLint] = useState<OverlayLint | null>(overlay.lint);
  const [linting, setLinting] = useState(false);
  const [example, setExample] = useState({ input: "", output: "" });
  const [comment, setComment] = useState("");
  const saveTimer = useRef<number | null>(null);
  const lintTimer = useRef<number | null>(null);
  const savedText = useRef(overlay.text);
  useEffect(() => { if (overlay.text !== savedText.current) { savedText.current = overlay.text; setText(overlay.text); } setLint(overlay.lint); }, [overlay.text, overlay.lint]);
  useEffect(() => () => { if (saveTimer.current) window.clearTimeout(saveTimer.current); if (lintTimer.current) window.clearTimeout(lintTimer.current); }, []);

  const runLint = async () => {
    setLinting(true);
    try { const r = await api.lintOverlay(role); setLint(r.lint); await refresh(); }
    catch (e) { if (e instanceof ApiError) toast(e.detail, "warn"); }
    finally { setLinting(false); }
  };
  const scheduleLint = () => { if (lintTimer.current) window.clearTimeout(lintTimer.current); lintTimer.current = window.setTimeout(() => void runLint(), LINT_DEBOUNCE_MS); };
  const save = async (value: string) => {
    if (value === savedText.current) return;
    savedText.current = value;
    const d = await apply([{ op: "replace", path: `/overlays/${role}/text`, value }]);
    if (d && value.trim()) scheduleLint();
    if (d && !value.trim()) setLint(null);
  };
  const onChange = (value: string) => {
    setText(value);
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => void save(value), SAVE_DEBOUNCE_MS);
  };
  const stale = !!text.trim() && (!lint || (overlay.lint && lint.linted_hash !== overlay.lint.linted_hash));
  const comments = (shown.comments ?? []).filter((c) => c.role === role);
  const canComment = overview.draft && (overview.role === "sme" || overview.role === "configurator");
  const postComment = async () => { try { await api.commentOnAgentDraft(role, comment); setComment(""); await refresh(); } catch (e) { toast(e instanceof ApiError ? e.detail : String(e), "warn"); } };
  const core = cores?.cores[role];
  return (
    <div className="overlay-editor">
      {core && <details className="core-panel"><summary>Core instructions (protected; your overlay is inserted below them)</summary><pre className="small">{core}</pre>{cores && <p className="muted small">Your text appears under: <em>{cores.tenant_heading}</em></p>}</details>}
      <div className="row wrap overlay-body">
        <div className="overlay-main">
          {editable ? (
            <Field label="Tenant instructions (markdown, up to 4000 characters)">
              <textarea rows={6} aria-label={`Overlay for ${AGENT_LABEL[role].name}`} value={text} onChange={(e) => onChange(e.target.value)} onBlur={() => { if (saveTimer.current) { window.clearTimeout(saveTimer.current); saveTimer.current = null; } void save(text); }} maxLength={4000} />
            </Field>
          ) : text.trim() ? <div className="md overlay-text"><Markdown text={text} /></div> : <p className="muted small">No tenant instructions for this agent.</p>}
          {editable && text.trim() && (
            <div className="row lint-row" aria-live="polite">
              {linting ? <span className="muted small"><span className="pulse" aria-hidden /> Linting against the core…</span> : lint && !stale ? <LintBadge items={lint.items} at={lint.at} /> : <span className="muted small">Not linted since the last change.</span>}
              <Button variant="quiet" onClick={() => void runLint()} disabled={linting}>Lint now</Button>
            </div>
          )}
          {lint && lint.items.length > 0 && !stale && <ul className="lint-items">{lint.items.map((i, k) => <LintLine key={k} item={i} />)}</ul>}
          <Examples role={role} examples={overlay.examples} editable={editable} example={example} setExample={setExample} />
        </div>
        {(comments.length > 0 || canComment) && (
          <aside className="overlay-comments">
            <h4 className="small">Comments</h4>
            {comments.length === 0 && <p className="muted small">None yet.</p>}
            <ul>{comments.map((c, i) => <li key={i} className="small"><strong>{c.by}</strong> <span className="muted">{relativeTime(c.at)}</span><p>{c.text}</p></li>)}</ul>
            {canComment && <form onSubmit={(e) => { e.preventDefault(); void postComment(); }}><textarea rows={2} aria-label={`Comment on ${AGENT_LABEL[role].name} instructions`} value={comment} onChange={(e) => setComment(e.target.value)} /><Button type="submit" variant="quiet" disabled={!comment.trim()}>Comment</Button></form>}
          </aside>
        )}
      </div>
    </div>
  );
}

function LintBadge({ items, at }: { items: LintItem[]; at: string }) {
  const blocking = items.filter((i) => i.severity === "blocking").length;
  const warn = items.length - blocking;
  return <span className="row small">{blocking ? <Badge tone="bad">{blocking} blocking</Badge> : warn ? <Badge tone="warn">{warn} warning{warn === 1 ? "" : "s"}</Badge> : <Badge tone="good">Lint clean</Badge>} <span className="muted">linted {relativeTime(at)}</span></span>;
}

function LintLine({ item }: { item: LintItem }) {
  return <li className={`lint-item ${item.severity}`}><Badge tone={item.severity === "blocking" ? "bad" : "warn"}>{item.severity}</Badge> {item.text}{item.quote && <> <q className="muted">{item.quote}</q></>}</li>;
}

function Examples({ role, examples, editable, example, setExample }: { role: AgentRole; examples: Array<{ input: string; output: string }>; editable: boolean; example: { input: string; output: string }; setExample: (e: { input: string; output: string }) => void }) {
  const { apply } = useAgents();
  if (!editable && !examples.length) return null;
  return (
    <div className="examples">
      <h4 className="small">Examples <span className="muted">(input → output pairs, up to 5)</span></h4>
      {examples.length === 0 && <p className="muted small">None.</p>}
      <ul>{examples.map((e, i) => <li key={i} className="small"><span className="ex-in">{e.input}</span> → <span className="ex-out">{e.output}</span>{editable && <button className="link small" aria-label={`Remove example ${i + 1} for ${AGENT_LABEL[role].name}`} onClick={() => void apply([{ op: "remove", path: `/overlays/${role}/examples/${i}` }])}>remove</button>}</li>)}</ul>
      {editable && examples.length < 5 && (
        <form className="row wrap" onSubmit={(e) => { e.preventDefault(); void apply([{ op: "add", path: `/overlays/${role}/examples/-`, value: { input: example.input.trim(), output: example.output.trim() } }]); setExample({ input: "", output: "" }); }}>
          <input aria-label={`Example input for ${AGENT_LABEL[role].name}`} placeholder="input" value={example.input} onChange={(e) => setExample({ ...example, input: e.target.value })} required />
          <input aria-label={`Example output for ${AGENT_LABEL[role].name}`} placeholder="output" value={example.output} onChange={(e) => setExample({ ...example, output: e.target.value })} required />
          <Button type="submit" variant="quiet">Add example</Button>
        </form>
      )}
    </div>
  );
}
